import { createSlice } from '@reduxjs/toolkit';
import {
  fetchWpHouse,
  fetchNotionHouse,
  fetchWpHouseDetails,
  fetchNotionHouseDetails,
} from '../thunks/house.thunks';
import { fetchCurrUser } from '../thunks/users.thunks';
const initialState = {
  user: null,
  data: [],
  originalData: [],
  sorted_data: [],
  originalSorted_data: [],
  filters: {
    location: {
      lat: '33.7488',
      lng: '-84.3877',
      radius: 100,
    },
    price_range: {
      min: 0,
      max: 5000000,
    },
    beds_range: {
      min: 0,
      max: 20,
    },
    sleeps_range: {
      min: 0,
      max: 20,
    },
    baths_range: {
      min: 0,
      max: 20,
    },
    size_sqft_range: {
      min: 0,
      max: 100000,
    },
    homeType: [],
    unitType: [],
    propertyFeatures: [],
    rentalPropertyFeatures: [],
  },
  searchTerm: '',
  sortBy: 'Closest to Me',
  formId: null,
  isFilter: false,
  isLoading: false,
  isFavoriteLoading: false,
  isError: null,
};

const housingForSale = createSlice({
  name: 'housingForSale',
  initialState,
  reducers: {
    setUser(state, action) {
      state.user = action.payload;
    },
    changeLocation(state, action) {
      state.filters.location.lat = action.payload.lat;
      state.filters.location.lng = action.payload.lng;
    },
    changeRadius(state, action) {
      state.filters.location.radius = parseInt(action.payload);
    },
    changePrice(state, action) {
      state.filters.price_range.min = action.payload.min;
      state.filters.price_range.max = action.payload.max;
    },
    changeSizeSqFt(state, action) {
      state.filters.size_sqft_range.min = action.payload.min;
      state.filters.size_sqft_range.max = action.payload.max;
    },
    changeBeds(state, action) {
      state.filters.beds_range.min = action.payload.min;
      state.filters.beds_range.max = action.payload.max;
    },
    changeHomeType(state, action) {
      state.filters.homeType = action.payload;
    },
    changePropertyFeatures(state, action) {
      state.filters.propertyFeatures = action.payload;
    },
    setLoading(state, action) {
      state.isLoading = action.payload;
    },
    setSortBy(state, action) {
      state.sortBy = action.payload;
      state.searchTerm = '';

      switch (action.payload) {
        case 'Closest to Me':
          state.sorted_data = [];
          state.originalSorted_data = [];
          break;

        case 'Price (low-high)':
          const priceLowHighSorted = [...state.data].sort((a, b) => {
            const aPrice = Number(a?.meta?.hp_price || a?.mlsListingPrice);
            const bPrice = Number(b?.meta?.hp_price || b?.mlsListingPrice);

            return aPrice - bPrice;
          });

          state.sorted_data = priceLowHighSorted;
          state.originalSorted_data = priceLowHighSorted;
          break;

        case 'Price (high-low)':
          const priceHighLowSorted = [...state.data].sort((a, b) => {
            const aPrice = Number(a?.meta?.hp_price || a?.mlsListingPrice);
            const bPrice = Number(b?.meta?.hp_price || b?.mlsListingPrice);

            return bPrice - aPrice;
          });

          state.sorted_data = priceHighLowSorted;
          state.originalSorted_data = priceHighLowSorted;
          break;

        case 'Recently Posted':
          const sortedArr = [...state.data].sort((a, b) => {
            const aDate = new Date(a.date).getTime() || 0;
            const aFirstSeen = new Date(a.lastUpdateDate).getTime() || 0;
            const bDate = new Date(b.date).getTime() || 0;
            const bFirstSeen = new Date(b.lastUpdateDate).getTime() || 0;

            return bDate + bFirstSeen - (aDate + aFirstSeen); // Sort by most recent combined date
          });

          state.sorted_data = sortedArr;
          state.originalSorted_data = sortedArr;
          break;

        default:
          state.sorted_data = [...state.originalData]; // Ensure to spread the array to avoid direct mutation
      }
    },
    setSearchTerm(state, action) {
      state.searchTerm = action.payload;
    },
    setDataBySearch(state, action) {
      switch (state.sortBy) {
        case 'Price (low-high)':
          const sorted1 = action.payload.sort((a, b) => {
            const aPrice = Number(a?.meta?.hp_price || a?.mlsListingPrice);
            const bPrice = Number(b?.meta?.hp_price || b?.mlsListingPrice);

            if (aPrice === bPrice) {
              return 0;
            } else {
              return aPrice < bPrice ? -1 : 1;
            }
          });

          if (action.payload.length > 0) {
            state.sorted_data = sorted1;
          } else {
            state.sorted_data = [];
          }
          break;

        case 'Price (high-low)':
          const sorted2 = action.payload.sort((a, b) => {
            const aPrice = Number(a?.meta?.hp_price || a.mlsListingPrice);
            const bPrice = Number(b?.meta?.hp_price || b.mlsListingPrice);

            if (aPrice === bPrice) {
              return 0;
            } else {
              return bPrice - aPrice;
            }
          });

          if (action.payload.length > 0) {
            state.sorted_data = sorted2;
          } else {
            state.sorted_data = [];
          }
          break;

        case 'Recently Posted':
          // Extract and parse dates from each object
          const allDates = action.payload.map((item) => ({
            date: new Date(item.date),
            lastUpdateDate: new Date(item.lastUpdateDate),
          }));

          // Flatten the array of dates
          const mergedDates = allDates.reduce((acc, curr) => {
            acc.push(curr.date, curr.lastUpdateDate);
            return acc;
          }, []);

          // Sort merged dates array by most recent date
          mergedDates.sort((a, b) => b.getTime() - a.getTime());

          // Reorder original array based on sorted dates
          const sorted4 = action.payload.sort((a, b) => {
            const aDate = new Date(a.date).getTime() || 0;
            const aFirstSeen = new Date(a.lastUpdateDate).getTime() || 0;
            const bDate = new Date(b.date).getTime() || 0;
            const bFirstSeen = new Date(b.lastUpdateDate).getTime() || 0;

            const aCombined = aDate + aFirstSeen;
            const bCombined = bDate + bFirstSeen;

            return bCombined - aCombined; // Sort by most recent combined date
          });
          if (action.payload.length > 0) {
            state.sorted_data = sorted4;
          } else {
            state.sorted_data = [];
          }
          break;

        default:
          state.data = action.payload;
      }
    },
    activateFilter(state, action) {
      state.isFilter = action.payload;
    },
    resetFilter(state, action) {
      state.filters = [];
      state.sorted_data = [];
      state.originalSorted_data = [];
    },
    resetSortedData(state, action) {
      state.sorted_data = [];
      state.originalSorted_data = [];
    },
    reset(state, action) {
      state.data = [];
      state.originalData = [];
      state.sorted_data = [];
      state.originalSorted_data = [];
      state.carDetails = [];
      state.vendorDetails = [];
      state.user = null;
    },
  },
  extraReducers(builder) {
    // FETCH CURRENT USER
    builder.addCase(fetchCurrUser.pending, (state, action) => {
      state.isLoading = true;
    });
    builder.addCase(fetchCurrUser.fulfilled, (state, action) => {
      state.user = action.payload;
      state.isLoading = false;
    });
    builder.addCase(fetchCurrUser.rejected, (state, action) => {
      state.isLoading = false;
      console.log(action.error);
    });

    // FETCH WP HOUSE
    builder.addCase(fetchWpHouse.pending, (state, action) => {
      state.isLoading = true;
    });
    builder.addCase(fetchWpHouse.fulfilled, (state, action) => {
      state.isLoading = true;
      let wpData = [];
      const curPos = state.filters.location;

      // Filter results by location
      const haversineDistance = (coords1, coords2) => {
        const toRad = (x) => (x * Math.PI) / 180;
        const R = 3958.8; // Radius of the Earth in miles

        const dLat = toRad(coords2.lat - coords1.lat);
        const dLng = toRad(coords2.lng - coords1.lng);

        const a =
          Math.sin(dLat / 2) * Math.sin(dLat / 2) +
          Math.cos(toRad(coords1.lat)) *
            Math.cos(toRad(coords2.lat)) *
            Math.sin(dLng / 2) *
            Math.sin(dLng / 2);

        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

        return R * c;
      };

      wpData = action.payload.data.filter((car) => {
        const carCoords = {
          lat: parseFloat(car.meta.hp_latitude),
          lng: parseFloat(car.meta.hp_longitude),
        };
        const distance = haversineDistance(curPos, carCoords);
        return distance <= curPos.radius;
      });

      if (wpData.length === 0) {
        state.isLoading = false;
        return;
      }

      // Filter by Price
      const priceMin = Number(state.filters.price_range.min);
      const priceMax = Number(state.filters.price_range.max);

      const priceFiltered = wpData.filter(
        (data) =>
          Number(data?.meta?.hp_price) >= priceMin &&
          Number(data?.meta?.hp_price) <= priceMax
      );
      wpData = priceFiltered;

      // Filter by Beds
      const bedsMin = Number(state.filters.beds_range.min);
      const bedsMax = Number(state.filters.beds_range.max);

      const bedsFiltered = wpData.filter(
        (data) =>
          Number(data?.meta?.hp_h_fs_beds) >= bedsMin &&
          Number(data?.meta?.hp_h_fs_beds) <= bedsMax
      );
      wpData = bedsFiltered;

      // Filter by Size sq.ft.
      const sizeSqFtMin = Number(state.filters.size_sqft_range.min);
      const sizeSqFtMax = Number(state.filters.size_sqft_range.max);

      const sizeSqFtFiltered = wpData.filter(
        (data) =>
          Number(data?.meta?.hp_size_sq_ft) >= sizeSqFtMin &&
          Number(data?.meta?.hp_size_sq_ft) <= sizeSqFtMax
      );
      wpData = sizeSqFtFiltered;

      // Filter by Home Type
      if (state.filters.homeType.length > 0) {
        const homeTypeFiltered = wpData.filter((data) =>
          state.filters.homeType
            .toString()
            .includes(data?.hp_listing_home_type[0]?.name)
        );
        wpData = homeTypeFiltered;
      }

      // Filter by Property Features
      if (state.filters.propertyFeatures.length > 0) {
        const featuresToFind = state.filters.propertyFeatures;
        const propertyFeaturesFiltered = wpData.filter(
          (item) =>
            item.hp_listing_h_fs_property_feature &&
            item.hp_listing_h_fs_property_feature.some((feature) =>
              featuresToFind.includes(feature.name)
            )
        );
        wpData = propertyFeaturesFiltered;
      }

      // Final Data
      state.data = [...state.data, wpData];
    });
    builder.addCase(fetchWpHouse.rejected, (state, action) => {
      state.isLoading = false;
      state.sorted_data = [];
      state.originalData = [];
      console.log(action.error);
    });

    // FETCH REALTY API
    builder.addCase(fetchNotionHouse.pending, (state, action) => {
      state.isLoading = true;
    });
    builder.addCase(fetchNotionHouse.fulfilled, (state, action) => {
      // check if
      if (action.payload?.data?.data) {
        const allHouse = [...state.data, action.payload?.data?.data];
        state.isLoading = false;
        state.data = allHouse.flat();
        state.originalData = state.data;
        console.log(action.payload?.data?.data);
      } else {
        state.isLoading = false;
        state.data = state.data.flat();
        state.originalData = state.data;
      }
    });
    builder.addCase(fetchNotionHouse.rejected, (state, action) => {
      state.isLoading = false;
      state.data = state.data.flat();
      state.originalData = state.data;
    });

    // FETCH WP CAR DETAILS
    builder.addCase(fetchWpHouseDetails.pending, (state, action) => {
      state.isLoading = true;
    });

    builder.addCase(fetchWpHouseDetails.fulfilled, (state, action) => {
      state.isLoading = false;
      state.carDetails = action.payload.data;
    });

    builder.addCase(fetchWpHouseDetails.rejected, (state, action) => {
      state.isLoading = false;
      state.isError = true;
    });

    // FETCH MC CAR DETAILS
    builder.addCase(fetchNotionHouseDetails.pending, (state, action) => {
      state.isLoading = true;
    });

    builder.addCase(fetchNotionHouseDetails.fulfilled, (state, action) => {
      state.isLoading = false;
      state.carDetails = action.payload.data;
    });

    builder.addCase(fetchNotionHouseDetails.rejected, (state, action) => {
      state.isLoading = false;
      state.isError = true;
    });
  },
});

export const housingForSaleReducer = housingForSale.reducer;
export const {
  setUser,
  changeLocation,
  changeRadius,
  changePrice,
  changeBeds,
  changeSizeSqFt,
  changeHomeType,
  changePropertyFeatures,
  setLoading,
  setSortBy,
  setSearchTerm,
  setDataBySearch,
  selectFilteredItems,
  activateFilter,
  resetFilter,
  resetSortedData,
  reset,
} = housingForSale.actions;
